import { ROUTE } from '../../../../routes';
import { artStyleLabel } from '../../../art-style/useActiveArtStyle';
import type { WebsiteCardBody, WebsiteCardIntent, WebsiteCardSummary } from '../../card-summary';
import type { ReadStatus } from '../creatives/creatives-summary';

/**
 * What the Art Style card says, from what its hook read: the brand's style list, the primary style's palette (from its
 * record), and whether a sample draw or an extraction of this brand is in flight. Pure: copy is an id, mapped to its
 * key in `art-style-copy.ts`.
 */

export type ArtStyleCardInput = {
  /** The style list read. */
  status: ReadStatus;
  styles: Array<{ id: string; name: string; active: boolean; hasDefinition: boolean; thumbUrl: string }>;
  /** The primary style's record palette (hex), null while unread or when its read failed. */
  palette: string[] | null;
  /** An `art_style_extract` or `art_style_sample` job of this brand is queued or running. */
  drawing: boolean;
};

export type ArtStyleCopy =
  | { id: 'drawing-samples' }
  | { id: 'no-primary-style' }
  | { id: 'style-name'; name: string }
  | { id: 'set-up' }
  | { id: 'set-up-style' }
  | { id: 'open-style'; name: string }
  | { id: 'choose-a-style' }
  | { id: 'open-art-style' };

type Style = ArtStyleCardInput['styles'][number];

/** The style spark draws with: active and defined, the rule `useActiveArtStyleState` applies (a brand has at most one). */
export const primaryArtStyle = (styles: ArtStyleCardInput['styles']): Style | null =>
  styles.find((s) => s.active && s.hasDefinition) ?? null;

const OPEN_ART_STYLE: WebsiteCardIntent<ArtStyleCopy> = { copy: { id: 'open-art-style' }, to: ROUTE.artStyle() };

export const deriveArtStyleSummary = (input: ArtStyleCardInput): WebsiteCardSummary<ArtStyleCopy> => {
  const { status, styles, palette, drawing } = input;
  const chip = drawing ? { tone: 'working' as const, copy: { id: 'drawing-samples' as const } } : null;
  const card = (
    body: WebsiteCardBody<ArtStyleCopy>,
    primary: WebsiteCardIntent<ArtStyleCopy>,
  ): WebsiteCardSummary<ArtStyleCopy> => ({ chip, body, primary, secondary: null });

  if (status === 'loading') return card({ kind: 'loading' }, OPEN_ART_STYLE);
  if (status === 'failed') return card({ kind: 'failed' }, OPEN_ART_STYLE);
  // The page makes the brand's first draft itself when it opens; the hub only links there.
  if (styles.length === 0) {
    return card(
      { kind: 'get-started', line: { id: 'set-up' } },
      { copy: { id: 'set-up-style' }, to: ROUTE.artStyle() },
    );
  }

  const primary = primaryArtStyle(styles);
  if (!primary) {
    return card(
      { kind: 'waiting', lines: [{ id: 'no-primary-style' }] },
      { copy: { id: 'choose-a-style' }, to: ROUTE.artStyle() },
    );
  }

  const name = artStyleLabel(primary.name);
  return card(
    {
      kind: 'context',
      line: { id: 'style-name', name },
      // Every colour: the card drops the ones it cannot paint, then shows the first six.
      art: { thumbUrl: primary.thumbUrl || undefined, swatches: palette ?? [] },
    },
    { copy: { id: 'open-style', name }, to: ROUTE.artStyleFor(primary.id) },
  );
};
